import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import clsx from 'clsx'
import { planSeed } from 'shared/plan-seed'
import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import { PlanCard } from '~/boundedContext/subscription/components/plan-card'
import { LinkButton } from '~/components/link-button'
import { LS_JWT_KEY } from '~/const/api'
import { useErrorFromSearchParams } from '~/hooks/use-error-from-search-params'
import { useAccountStore } from '~/stores/account'
import { extractTokenPayload } from '~/utils/extract-token-payload'

export const PlanSelectionPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  // TODO: find better way
  const jwtPayload = useMemo(() => extractTokenPayload(), [])
  const patchAccount = useAccountStore(state => state.patchAccount)

  useErrorFromSearchParams()

  useEffect(() => {
    if (!jwtPayload) {
      void navigate({ to: '/auth/login' })

      return
    }

    if (jwtPayload?.subscriptionStatus === 'ACTIVE') {
      void navigate({ to: '/cards' })

      return
    }
  }, [jwtPayload, navigate])

  return (
    <div className="fixed flex h-full w-full flex-col items-center gap-6 overflow-auto p-6">
      <header className="text-center">
        <h1 className="text-4xl font-bold">{t('Select Your Plan')}</h1>
        <p className="text-lg text-gray-500">{t('Choose the plan that best suits you')}</p>
      </header>
      <section className="flex w-full flex-wrap justify-center gap-4 md:gap-8">
        {planSeed.map(plan => (
          <PlanCard
            key={plan.id}
            className={clsx(
              plan.name === 'Basic Plan' &&
                'bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900',
            )}
            coins={plan.coins}
            onSelect={async () => {
              try {
                const { data } = await apiClient.POST('/subscription', {
                  body: { planId: plan.id },
                })
                if (!data) {
                  toast.error(t('Something went wrong'))

                  return
                }

                if ('jwtToken' in data && data.jwtToken) {
                  localStorage.setItem(LS_JWT_KEY, data.jwtToken)

                  patchAccount({ coins: data.coins, planId: plan.id, planCoins: data.coins })
                  return navigate({ to: '/cards' })
                }

                if ('clientSecret' in data && data.clientSecret)
                  void navigate({ to: '/subscription/checkout', search: { clientSecret: data.clientSecret } })
              } catch (error) {
                toast.error(t('Network error'))
                console.error({ message: 'Network error', error })
                return
              }
            }}
            price={plan.price}
            title={t(plan.name)}
          />
        ))}
      </section>
      <section className="flex max-w-md flex-col gap-4">
        <LinkButton to="/auth/login">{t('To login page')}</LinkButton>
      </section>
    </div>
  )
}
